import { Ellipsis } from 'lucide-react';
import { ReactNode, useState } from 'react';
import { RowWithId } from '@/components/table/types/RowWithId.ts';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu.tsx';
import { cn } from '@/lib/utils.ts';

type BaseMenuItemParams<T extends RowWithId> = {
  action: (rowData: T) => void;
  visible?: (rowData: T) => boolean;
  disabled?: (rowData: T) => boolean;
  disabledTooltip?: (rowData: T) => string | undefined;
};

type SimpleMenuItem<T extends RowWithId> = BaseMenuItemParams<T> & {
  label: string | ReactNode;
  variant?: 'default' | 'destructive';
};

type CustomMenuItem<T extends RowWithId> = BaseMenuItemParams<T> & {
  customRender: (rowData: T) => ReactNode;
};

export type RowContextMenuItemParams<T extends RowWithId> =
  | SimpleMenuItem<T>
  | CustomMenuItem<T>;

export function isCustomMenuItem<T extends RowWithId>(
  item: RowContextMenuItemParams<T>,
): item is CustomMenuItem<T> {
  return 'customRender' in item;
}

const RowContextMenu = <T extends RowWithId>({
  rowData,
  rowContextMenuItems,
}: {
  rowData: T;
  rowContextMenuItems: RowContextMenuItemParams<T>[];
}) => {
  const [open, setOpen] = useState(false);

  const handleAction = (action: () => void) => {
    action();
    setOpen(false);
  };

  const visibleItems = rowContextMenuItems.filter(
    (item) => item.visible === undefined || item.visible(rowData),
  );

  if (visibleItems.length === 0) return null;

  const renderItem = (item: RowContextMenuItemParams<T>, index: number) => {
    const isDisabled = item.disabled?.(rowData) ?? false;
    const disabledTooltip = item.disabledTooltip?.(rowData);

    if (isCustomMenuItem(item)) {
      return (
        <button
          key={index}
          onClick={() => handleAction(() => item.action(rowData))}
          disabled={isDisabled}
          className="w-full"
          title={isDisabled ? disabledTooltip : undefined}
        >
          {item.customRender(rowData)}
        </button>
      );
    }

    if (isDisabled && disabledTooltip) {
      // Bez `disabled` Radiksa, bo ten dokłada `pointer-events-none` i zabija
      // dymek z `title`; stan niesie za to `aria-disabled`.
      return (
        <DropdownMenuItem
          key={index}
          aria-disabled="true"
          className={cn('cursor-default opacity-50', {
            'text-error-200': item.variant === 'destructive',
          })}
          title={disabledTooltip}
          onSelect={(e) => e.preventDefault()}
        >
          {item.label}
        </DropdownMenuItem>
      );
    }

    return (
      <DropdownMenuItem
        key={index}
        onClick={() => handleAction(() => item.action(rowData))}
        disabled={isDisabled}
        className={cn({
          'text-error-200': item.variant === 'destructive',
        })}
      >
        {item.label}
      </DropdownMenuItem>
    );
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        {/* Tło przezroczyste, bo komórka menu jest przyklejona i sama odbija
            barwę wiersza — biała plamka rozjeżdżała się z hoverem i zaznaczeniem. */}
        <button className="box-border h-7 rounded-sm border border-transparent bg-transparent p-[5px] text-neutral-600 hover:border-neutral-200 hover:bg-accent-200 hover:text-accent-700">
          <Ellipsis className="size-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-48 p-1" align="end">
        {visibleItems.map(renderItem)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default RowContextMenu;
